import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import RecordingConsentSheet from './RecordingConsentSheet'
import { useI18n } from '../i18n'
import { appointmentsAPI } from '../services/api'
import {
  canAskRecordingConsent,
  consentAppointmentId,
  joinIsOpen,
  joinOpensAt,
  joinPlan,
  joinVideoVisitLabelKey,
  MEET_HINT_KEY,
  IOS_MEET_HINT_KEY,
  showIosMeetHint,
  showMeetHint,
  storedConsentDecision,
  submitConsentThenOpen,
  visitMeetingUrl,
} from '../utils/videoVisit'

/**
 * JoinVideoVisit — MEET-patient PA1 (PROD-538, MEET_PLAN 3.3 + MEET_PLAN_UPDATE §3).
 *
 * The one Join button of a video visit. A tap:
 *   - a Google Meet visit with no recording decision, or one the patient declined earlier, opens
 *     the pre-join sheet FIRST ("Agree and join" / "Join without recording");
 *   - the chosen decision is POSTed (/appointments/<id>/recording-consent) and only a SAVED
 *     decision opens the link. ★★ A failed POST opens nothing: the sheet stays with
 *     "We could not save your choice. Please try again.";
 *   - an accepted record, a Zoom row (nothing to consent to) or a language without approved text
 *     (Spanish until Dr. Rami approves) opens the link directly; not asked = not recorded.
 * The decisions live in src/utils/videoVisit.js; scripts/check-join-consent.mjs holds them.
 *
 * Props: appointment (the row or appointment object carrying the link, id, meeting_provider,
 *   recordingConsent; a join point builds it with videoJoinTarget), appointmentId (when the object
 *   carries none), className / style for the button, icon / trailing (nodes drawn before / after
 *   the label), children (replaces the label; default "Join video visit").
 * Renders nothing when the appointment carries no https link.
 * ★ PA2: every join point of the app (VisitsScreen, DiagnoseScreen, DeliverScreen, CheckInModal)
 *   opens a visit link only through this component; check-join-consent J5 holds that.
 * ★ No appointment id = no decision can be saved: the sheet is not drawn and the link opens
 *   (not asked = not recorded), never a dead button. PA6: an id that is not a uuid (a synthesized
 *   'followup-<case>' / 'initial-<case>' row of GET /appointments) counts as no id.
 */

/** Open a visit link: Capacitor Browser on native, a new window on the web (MEET_PLAN PA1).
 *  -> true, or false when the browser refused to open a window. On the web window.open runs
 *  before any await, so a direct tap keeps its user gesture. */
export async function openVisitLink(url) {
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url })
    return true
  }
  const w = window.open(url, '_blank')
  if (!w) return false
  try { w.opener = null } catch { /* a cross-origin window may refuse; nothing to do */ }
  return true
}

/**
 * s126c (Dr. Rami 2026-11-01): two more rules on every Join.
 *   - The 15-minute window: before (start − 15 min) the button is held and says when it opens
 *     (videoVisit.joinOpensAt / joinOpensBefore); no readable start = not held. It re-checks on its own.
 *   - beforeOpen(proceed): a caller that must run something first (the video sign-on, or the check-in that
 *     precedes it) gets the tap and calls proceed() to continue the join. It is handed only for a visit whose
 *     sign-on can be recorded (a uuid row); a synthesized row joins as before.
 *   - autoJoin: CheckInModal opened FROM a Join tap starts the join itself once it is on its last screen.
 */
export default function JoinVideoVisit({ appointment, appointmentId = null, className = '', style, icon = null, trailing = null, children = null, beforeOpen = null, autoJoin = false }) {
  const { t, hasText, language } = useI18n()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [decidedNow, setDecidedNow] = useState(null)
  const [blocked, setBlocked] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const autoFired = useRef(false)

  const url = visitMeetingUrl(appointment)
  const opensAt = joinOpensAt(appointment)
  const opensAtMs = opensAt ? opensAt.getTime() : null
  const open = joinIsOpen(appointment, now)
  // PA6 (R3 F7): only a uuid is an id the consent route knows; a synthesized 'followup-<case>' /
  // 'initial-<case>' row counts as no id, so the sheet is not drawn and the link opens.
  const id = consentAppointmentId(appointment, appointmentId)
  const canAsk = canAskRecordingConsent(hasText) && !!id
  const decision = decidedNow || storedConsentDecision(appointment)

  const onJoin = () => {
    const plan = joinPlan(appointment, { canAsk, decidedNow })
    if (plan === 'ask') {
      setSaveFailed(false)
      setBlocked(false)
      setSheetOpen(true)
      return
    }
    if (plan === 'open') openVisitLink(url)
  }
  // s126c: the tap — held before the window; a caller's beforeOpen (sign-on / check-in) runs first
  const onTap = () => {
    if (!joinIsOpen(appointment, Date.now())) return
    if (typeof beforeOpen === 'function' && id) { beforeOpen(onJoin); return }
    onJoin()
  }

  // s126c: a held Join re-checks when the window opens (at most a minute between looks).
  useEffect(() => {
    if (open || opensAtMs === null) return undefined
    const wait = Math.min(Math.max(opensAtMs - Date.now(), 1000), 60000)
    const timer = setTimeout(() => setNow(Date.now()), wait)
    return () => clearTimeout(timer)
  }, [open, opensAtMs, now])

  // s126c: CheckInModal opened from a Join tap joins once, as soon as Join is allowed.
  useEffect(() => {
    if (!autoJoin || !url || !open || autoFired.current) return
    autoFired.current = true
    onTap()
  }, [autoJoin, url, open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!url) return null

  const choose = async (choice) => {
    if (busy) return
    setBusy(true)
    setSaveFailed(false)
    const res = await submitConsentThenOpen({
      appointmentId: id,
      url,
      decision: choice,
      postConsent: appointmentsAPI.recordingConsent,
      openUrl: openVisitLink,
    })
    setBusy(false)
    if (!res.saved) { setSaveFailed(true); return }
    setDecidedNow(choice)
    if (res.opened) setSheetOpen(false)
    else setBlocked(true)
  }

  // Under es, until Dr. Rami approves visits.joinVideoVisit, the approved neutral label
  // diagnose.joinVideoCall stays (joinVideoVisitLabelKey; PA3 retired visits.joinZoomMeeting).
  const label = children || t(joinVideoVisitLabelKey(hasText))

  // PROD-1108: iPhone Safari cannot join Meet in the browser; tell the patient to tap "Open app".
  const iosHint = showIosMeetHint(
    appointment,
    hasText,
    typeof navigator === 'undefined' ? null : { userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints },
    Capacitor.isNativePlatform(),
  )

  return (
    <>
      {iosHint && (
        <p className="mt-1 mb-2 text-[11px]" style={{ color: '#5F7A8E' }} data-ios-meet-hint="">{t(IOS_MEET_HINT_KEY)}</p>
      )}
      <button type="button" onClick={onTap} className={className} disabled={!open} aria-disabled={!open}
        style={open ? style : { ...(style || {}), opacity: 0.45, cursor: 'default' }} data-join-video-visit="">
        {icon}{label}{trailing}
      </button>
      {!open && opensAt && (
        <p className="mt-1 mb-2 text-[11.5px]" style={{ color: '#5F7A8E' }} data-join-opens="">
          {opensAt.toDateString() === new Date(now).toDateString()
            ? t('videoVisit.joinOpensAt', { time: opensAt.toLocaleTimeString(language === 'es' ? 'es-US' : [], { hour: 'numeric', minute: '2-digit' }) })
            : t('videoVisit.joinOpensBefore')}
        </p>
      )}
      {/* PA3 (MEET_PLAN 3.7, F-M8): "Your visit opens in Google Meet." under a Meet visit's Join;
          not under a Zoom visit, not in a language without approved text. */}
      {showMeetHint(appointment, hasText) && (
        <p className="mt-1 mb-2 text-[11px]" style={{ color: '#5F7A8E' }} data-meet-hint="">{t(MEET_HINT_KEY)}</p>
      )}
      <RecordingConsentSheet
        open={sheetOpen}
        busy={busy}
        error={saveFailed ? t('videoVisit.saveFailed') : null}
        acceptLabel={t('videoVisit.agreeAndJoin')}
        declineLabel={t('videoVisit.joinWithoutRecording')}
        onAccept={() => choose('accepted')}
        onDecline={() => choose('declined')}
        onClose={() => setSheetOpen(false)}
      >
        {decision === 'declined' && (
          <p className="mt-3 text-[12.5px]" style={{ color: '#5F7A8E' }}>{t('videoVisit.notRecorded')}</p>
        )}
        {blocked && (
          <button
            type="button"
            data-join-after-block=""
            onClick={() => { openVisitLink(url); setSheetOpen(false) }}
            className="mt-3 w-full rounded-xl py-3 text-[14.5px] font-bold text-white"
            style={{ background: '#15354F' }}
          >
            {t('visits.joinVideoVisit')}
          </button>
        )}
      </RecordingConsentSheet>
    </>
  )
}
